import React, { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { useTar } from '@/modules'
import { SidebarThemeButton } from '@/components/SideBar/SidebarThemeButton'
import { useSidebar, SidebarMenuItem, SidebarMenuButton, SidebarGroup, SidebarGroupContent, SidebarMenu, Button } from '@duck/ui'
import { UserAvatar } from '../UserAvatar'
import { ArrowRightEndOnRectangleIcon } from '@heroicons/react/24/outline'

export const SidebarUserArea: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const {
    user,
    isAuthenticated,
    displayName,
    signOut,
    openLoginDialog
  } = useAuth()
  const { open, isMobile, setOpenMobile } = useSidebar()
  const navigate = useNavigate()
  const { t } = useTar()

  const handleLogout = async () => {
    setIsLoading(true)
    try {
      await signOut()
      navigate({ to: '/' })
      if (isMobile) {
        setOpenMobile(false)
      }
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogin = () => {
    openLoginDialog()
  }

  // If not authenticated, show login option
  if (!isAuthenticated) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="flex items-center gap-2">
            <SidebarMenu className="min-w-0 flex-1">
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Button
                    onClick={handleLogin}
                    disabled={isLoading}
                    variant="ghost"
                    className="flex items-center hover:bg-accent/10 hover:text-foreground w-full text-muted-foreground justify-start h-9"
                  >
                    {/* Login arrow icon */}
                    <ArrowRightEndOnRectangleIcon className="!w-5 !h-5" aria-hidden="true" />
                    {open && (
                      <span className="text-xs">
                        {isLoading ? t('common.loading') : t('common.login')}
                      </span>
                    )}
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            {open && <SidebarThemeButton />}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  // If authenticated, show full user area
  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <div className={`${open ? 'space-y-3' : 'space-y-2'}`}>
          {/* User Info - full area clickable to user-area */}
          <div
            role="button"
            tabIndex={0}
            aria-label={displayName || user?.email || 'User area'}
            onClick={() => navigate({ to: '/user-area' })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                navigate({ to: '/user-area' })
              }
            }}
            className={`flex items-center ${open ? 'gap-3' : 'justify-center'} py-2 cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
          >
            {/* Avatar */}
            <div className="flex-shrink-0">
              <UserAvatar size={'md'} />
            </div>

            {/* Text info - only show when sidebar is open */}
            {open && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            )}
          </div>

          {/* Logout button + theme toggle */}
          {open && (
            <div className="flex items-center gap-2">
              <SidebarMenu className="min-w-0 flex-1">
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Button
                      onClick={handleLogout}
                      disabled={isLoading}
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground hover:text-destructive justify-start p-3 h-9"
                      title={t('common.logout')}
                    >
                      <svg className={`w-4 h-4`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {open && (
                        <span className="text-sm">
                          {isLoading ? t('common.loggingOut') : t('common.logout')}
                        </span>
                      )}
                    </Button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
              <SidebarThemeButton />
            </div>
          )}
        </div>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
